import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Building2, ListChecks, Plus, Trash2, Loader2, KeyRound, Pencil } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  fetchUsers, createUser, deleteUserApi,
  fetchSites, createSite, updateSite, deleteSiteApi,
  fetchLists, updateLists, changePassword,
} from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

const Card = ({ title, icon: Icon, action, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="bg-[#0A192F] px-5 py-3 flex items-center justify-between border-b-2 border-[#D4AF37]">
      <div className="flex items-center gap-2 text-white">
        <Icon className="h-5 w-5 text-[#D4AF37]" />
        <h3 className="font-serif-title text-base font-bold">{title}</h3>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ---------------- Users ----------------
const UsersSection = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "agent" });
  const [saving, setSaving] = useState(false);

  const load = () => fetchUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.email || form.password.length < 6) return toast.error("Email requis et mot de passe ≥ 6 caractères.");
    setSaving(true);
    try {
      await createUser(form);
      toast.success("Utilisateur créé.");
      setOpen(false); setForm({ email: "", password: "", name: "", role: "agent" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await deleteUserApi(id); toast.success("Utilisateur supprimé."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <Card title="Utilisateurs" icon={Users}
      action={<Button size="sm" className="bg-[#D4AF37] hover:bg-[#C59B27] text-[#0A192F] font-semibold gap-1" onClick={() => setOpen(true)} data-testid="add-user-btn"><Plus className="h-4 w-4" /> Ajouter</Button>}>
      <div className="space-y-2" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2" data-testid={`user-row-${u.id}`}>
            <div>
              <div className="font-semibold text-slate-800">{u.name || u.email}</div>
              <div className="text-xs text-slate-500">{u.email}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-[#F7EBAA] text-[#8B6B1B]" : "bg-slate-100 text-slate-600"}`}>{u.role}</span>
              {u.id !== user.id && (
                <Button size="icon" variant="ghost" className="h-9 w-9 text-red-600" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle><DialogDescription>Créez un compte agent ou administrateur.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase text-slate-500">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label className="text-xs uppercase text-slate-500">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div><Label className="text-xs uppercase text-slate-500">Mot de passe</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div><Label className="text-xs uppercase text-slate-500">Rôle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="agent">Agent</SelectItem><SelectItem value="admin">Administrateur</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={add} disabled={saving} className="bg-[#0A192F]" data-testid="save-user-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ---------------- Sites ----------------
const SitesSection = ({ onChanged }) => {
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", adresse: "", contact: "", actif: true });
  const [saving, setSaving] = useState(false);

  const load = () => fetchSites().then((s) => { setSites(s); onChanged && onChanged(); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", adresse: "", contact: "", actif: true }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, adresse: s.adresse || "", contact: s.contact || "", actif: s.actif !== false }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom du site requis.");
    setSaving(true);
    try {
      if (editing) await updateSite(editing.id, form);
      else await createSite(form);
      toast.success("Site enregistré.");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await deleteSiteApi(id); toast.success("Site supprimé."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <Card title="Sites / Clients SSG" icon={Building2}
      action={<Button size="sm" className="bg-[#D4AF37] hover:bg-[#C59B27] text-[#0A192F] font-semibold gap-1" onClick={openNew} data-testid="add-site-btn"><Plus className="h-4 w-4" /> Ajouter</Button>}>
      <div className="space-y-2" data-testid="sites-list">
        {sites.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Aucun site. Ajoutez votre premier client SSG.</p>}
        {sites.map((s) => (
          <div key={s.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2" data-testid={`site-row-${s.id}`}>
            <div>
              <div className="font-semibold text-slate-800">{s.name}</div>
              <div className="text-xs text-slate-500">{[s.adresse, s.contact].filter(Boolean).join(" · ") || "—"}</div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-500" onClick={() => openEdit(s)} data-testid={`edit-site-${s.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-9 w-9 text-red-600" onClick={() => remove(s.id)} data-testid={`delete-site-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="site-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifier le site" : "Nouveau site / client"}</DialogTitle><DialogDescription>Un site correspond à un client ou un lieu gardé par SSG.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase text-slate-500">Nom du site / client</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-name" placeholder="Ex : Siège BICIGUI - Kaloum" /></div>
            <div><Label className="text-xs uppercase text-slate-500">Adresse</Label><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} data-testid="site-adresse" /></div>
            <div><Label className="text-xs uppercase text-slate-500">Contact</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="site-contact" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0A192F]" data-testid="save-site-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ---------------- Lists ----------------
const ListsSection = ({ onChanged }) => {
  const [motifs, setMotifs] = useState([]);
  const [services, setServices] = useState([]);
  const [newMotif, setNewMotif] = useState("");
  const [newService, setNewService] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchLists().then((d) => { setMotifs(d.motifs); setServices(d.services); }).catch(() => {}); }, []);

  const save = async (m, s) => {
    setSaving(true);
    try { await updateLists({ motifs: m, services: s }); onChanged && onChanged(); toast.success("Listes mises à jour."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const Chips = ({ items, onRemove, testid }) => (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {items.map((it) => (
        <span key={it} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full pl-3 pr-1 py-1 text-sm">
          {it}
          <button className="h-5 w-5 rounded-full hover:bg-red-100 text-red-600 flex items-center justify-center" onClick={() => onRemove(it)}>×</button>
        </span>
      ))}
    </div>
  );

  return (
    <Card title="Listes (motifs & services)" icon={ListChecks}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Motifs de visite</Label>
          <Chips items={motifs} testid="motifs-chips" onRemove={(it) => { const m = motifs.filter((x) => x !== it); setMotifs(m); save(m, services); }} />
          <div className="flex gap-2">
            <Input value={newMotif} onChange={(e) => setNewMotif(e.target.value)} placeholder="Nouveau motif" data-testid="new-motif" />
            <Button className="bg-[#0A192F] shrink-0" onClick={() => { if (!newMotif.trim()) return; const m = [...motifs, newMotif.trim()]; setMotifs(m); setNewMotif(""); save(m, services); }} data-testid="add-motif-btn"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Services / départements</Label>
          <Chips items={services} testid="services-chips" onRemove={(it) => { const s = services.filter((x) => x !== it); setServices(s); save(motifs, s); }} />
          <div className="flex gap-2">
            <Input value={newService} onChange={(e) => setNewService(e.target.value)} placeholder="Nouveau service" data-testid="new-service" />
            <Button className="bg-[#0A192F] shrink-0" onClick={() => { if (!newService.trim()) return; const s = [...services, newService.trim()]; setServices(s); setNewService(""); save(motifs, s); }} data-testid="add-service-btn"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
      {saving && <p className="text-xs text-slate-400 mt-3">Enregistrement…</p>}
    </Card>
  );
};

// ---------------- Password ----------------
const PasswordSection = () => {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await changePassword(oldPw, newPw); toast.success("Mot de passe modifié."); setOldPw(""); setNewPw(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Card title="Mon mot de passe" icon={KeyRound}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
        <div><Label className="text-xs uppercase text-slate-500">Mot de passe actuel</Label><Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} data-testid="old-pw" /></div>
        <div><Label className="text-xs uppercase text-slate-500">Nouveau mot de passe</Label><Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-pw" /></div>
      </div>
      <Button onClick={submit} disabled={saving} className="mt-4 bg-[#0A192F]" data-testid="change-pw-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Modifier le mot de passe"}</Button>
    </Card>
  );
};

export const AdminPanel = ({ onSitesChanged, onListsChanged }) => {
  return (
    <div data-testid="admin-panel">
      <Tabs defaultValue="sites" className="w-full">
        <TabsList className="bg-white border border-slate-200 shadow-sm p-1 h-auto flex flex-wrap mb-5">
          <TabsTrigger value="sites" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white px-4 py-2 gap-2 text-sm font-semibold" data-testid="admin-tab-sites"><Building2 className="h-4 w-4" /> Sites / Clients</TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white px-4 py-2 gap-2 text-sm font-semibold" data-testid="admin-tab-users"><Users className="h-4 w-4" /> Utilisateurs</TabsTrigger>
          <TabsTrigger value="lists" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white px-4 py-2 gap-2 text-sm font-semibold" data-testid="admin-tab-lists"><ListChecks className="h-4 w-4" /> Listes</TabsTrigger>
          <TabsTrigger value="password" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white px-4 py-2 gap-2 text-sm font-semibold" data-testid="admin-tab-password"><KeyRound className="h-4 w-4" /> Sécurité</TabsTrigger>
        </TabsList>
        <TabsContent value="sites"><SitesSection onChanged={onSitesChanged} /></TabsContent>
        <TabsContent value="users"><UsersSection /></TabsContent>
        <TabsContent value="lists"><ListsSection onChanged={onListsChanged} /></TabsContent>
        <TabsContent value="password"><PasswordSection /></TabsContent>
      </Tabs>
    </div>
  );
};
